import React, { useEffect, useState } from "react";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import CreateInvitationForm from "./CreateInvitationForm";
import InvitationsFilters from "./InvitationsFilters";
import InvitationsTable from "./InvitationsTable";
import { Invitation, ROLE_OPTIONS } from "../../utils/hooks/multi-tenant/invitation";
import { getInvitations, deleteInvitation } from "../../utils/api/endpoints/user/invitation";
import { requestChangeUserRole } from "../../utils/api/endpoints/user/multiTenant";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import toast from "react-hot-toast";
import Pagination from "../retests/utils/Pagination";

const TeamManagementPanel: React.FC = () => {
  const { role } = useUserCredentials();

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [emailToUser, setEmailToUser] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [currentInvitationsPage, setCurrentInvitationsPage] = useState(1);
  const itemsPerPage = 6;

  const availableRoles = role ? ROLE_OPTIONS[role as UserRole] : [];

  const fetchInvitations = async () => {
    try {
      setIsLoading(true);
      const data = await getInvitations();
      setInvitations(data);
      // Build quick lookup from response Invitee, fallback to existing external map shape
      const map: Record<string, any> = {};
      for (const inv of data || []) {
        const key = (inv?.invitee_email || "").toLowerCase();
        if (!key) continue;
        if (inv?.Invitee) {
          map[key] = {
            username: inv.Invitee.username,
            displayName: inv.Invitee.username,
            email: inv.Invitee.email,
            profilePicture: inv.Invitee.pfp
          };
        }
      }
      setEmailToUser(map);
      setIsError(false);
    } catch (e) {
      setIsError(true);
      toast.error("Failed to load team members");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  const handleCreateInvitation = async () => {
    await fetchInvitations();
  };

  const handleRoleChange = async (invitationId: number, newRole: UserRole) => {
    try {
      await requestChangeUserRole({ invitation_id: invitationId, new_role: newRole });
      await fetchInvitations();
      toast.success("Role updated");
      window.dispatchEvent(new Event("ctb-activity-logs-refresh"));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to update role");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteInvitation(id);
      await fetchInvitations();
      toast.success("Removed successfully");
      window.dispatchEvent(new Event("ctb-activity-logs-refresh"));
    } catch {
      toast.error("Failed to remove");
    }
  };

  // Resend invite (Invite action in table)
  const handleResendInvitation = async (email: string, role: UserRole) => {
    const emailKey = (email || "").toLowerCase();
    console.log("Resend invitation clicked", { email, role });
    const toastId = toast.loading(`Sending invite to ${email}...`);
    // Optimistic: mark the matching row(s) as pending so UI reflects change immediately
    const prev = invitations;
    const next = invitations.map(inv =>
      inv.invitee_email?.toLowerCase() === emailKey
        ? { ...inv, status: "pending" as any }
        : inv
    );
    setInvitations(next);

    try {
      const { createInvitation } = await import("../../utils/api/endpoints/user/invitation");
      await createInvitation({ email, role });
      toast.success("Invitation sent", { id: toastId });
      // Refetch to ensure we show the latest status/expiry from server
      await fetchInvitations();
      window.dispatchEvent(new Event("ctb-activity-logs-refresh"));
    } catch (error: any) {
      console.error("Resend invite failed", error);
      // Revert optimistic update
      setInvitations(prev);
      const msg = error?.response?.data?.message || error?.message || "Invite failed";
      const code = error?.response?.status ? ` (HTTP ${error.response.status})` : "";
      toast.error(`${msg}${code}`, { id: toastId });
    }
  };

  // Apply filters
  // Order most recent first (by id desc as a proxy for recency)
  const ordered = [...invitations].sort((a: any, b: any) => (b?.id || 0) - (a?.id || 0));

  const filtered = ordered.filter(inv => {
    const roleOk = filterRole === "all" || UserRole[inv.role] === filterRole;
    const statusOk = filterStatus === "all" || inv.status.toLowerCase() === filterStatus.toLowerCase();
    const searchOk = inv.invitee_email.toLowerCase().includes(searchQuery.toLowerCase());
    return roleOk && statusOk && searchOk;
  });

  // reset to first page when filters/search change
  useEffect(() => {
    setCurrentInvitationsPage(1);
  }, [searchQuery, filterRole, filterStatus]);

  const totalInvitationsPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const paginatedInvitations = filtered.slice(
    (currentInvitationsPage - 1) * itemsPerPage,
    currentInvitationsPage * itemsPerPage
  );

  return (
    <>
      {/* Card 1 - Team Management */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm mb-6">
        <h2 className="text-xl text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>Team Management</h2>
        <CreateInvitationForm availableRoles={availableRoles} onInvitationCreated={handleCreateInvitation} />
        <InvitationsFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterRole={filterRole}
          setFilterRole={setFilterRole}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          roleFilterOptions={(availableRoles || []).map((r: any) => ({ value: String(UserRole[r.value as unknown as number] || r.value), label: r.label }))}
        />
        <InvitationsTable
          invitations={paginatedInvitations}
          isLoading={isLoading}
          isError={isError}
          availableRoles={availableRoles}
          onRoleChange={handleRoleChange}
          onDeleteClick={handleDelete}
          onResendInvitation={handleResendInvitation}
          externalUsersMap={emailToUser}
        />
        <Pagination
          currentPage={currentInvitationsPage}
          totalPages={totalInvitationsPages}
          onPageChange={setCurrentInvitationsPage}
        />
      </div>

      {/* Card 2 - Role Permissions */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>Role Permissions</h2>
        <div className="overflow-hidden rounded-lg border border-gray-200">
          {role === UserRole.ADMIN ? (
            <table className="min-w-full text-sm">
              <thead className="bg-[#010D2C]">
                <tr>
                  <th className="px-6 py-4 text-left font-medium text-white">Permission</th>
                  <th className="px-6 py-4 text-center font-medium text-white">Admin</th>
                  <th className="px-6 py-4 text-center font-medium text-white">Sub Admin</th>
                  <th className="px-6 py-4 text-center font-medium text-white">Admin Manager</th>
                  <th className="px-6 py-4 text-center font-medium text-white">QA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { perm: 'View Dashboard', ad: true, sa: true, am: true, qa: true },
                  { perm: 'View Reports', ad: true, sa: true, am: false, qa: true },
                  { perm: 'Retests', ad: true, sa: true, am: false, qa: true },
                  { perm: 'Programs', ad: true, sa: true, am: true, qa: true },
                  { perm: 'Users', ad: true, sa: true, am: true, qa: false },
                  { perm: 'Team Management', ad: true, sa: false, am: false, qa: false }
                ].map(row => (
                  <tr key={row.perm}>
                    <td className="px-6 py-4 text-gray-700 bg-white">{row.perm}</td>
                    {[row.ad, row.sa, row.am, row.qa].map((v, idx) => (
                      <td key={idx} className="px-6 py-4 text-center bg-white">
                        {v ? (
                          <svg className="mx-auto h-5 w-5 text-[#58CC02]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-[#010D2C]">
                <tr>
                  <th className="px-6 py-4 text-left font-medium text-white">Permission</th>
                  <th className="px-6 py-4 text-center font-medium text-white">Business Owner</th>
                  <th className="px-6 py-4 text-center font-medium text-white">Organization Admin</th>
                  <th className="px-6 py-4 text-center font-medium text-white">Program Manager</th>
                  <th className="px-6 py-4 text-center font-medium text-white">Security Engineer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { perm: 'View Dashboard', bo: true, oa: true, pm: true, se: true },
                  { perm: 'View Reports', bo: true, oa: true, pm: false, se: true },
                  { perm: 'Retest', bo: true, oa: true, pm: false, se: true },
                  { perm: 'Programs', bo: true, oa: true, pm: true, se: false },
                  { perm: 'Team Management', bo: true, oa: false, pm: false, se: false }
                ].map(row => (
                  <tr key={row.perm}>
                    <td className="px-6 py-4 text-gray-700 bg-white">{row.perm}</td>
                    {[row.bo, row.oa, row.pm, row.se].map((v, idx) => (
                      <td key={idx} className="px-6 py-4 text-center bg-white">
                        {v ? (
                          <svg className="mx-auto h-5 w-5 text-[#58CC02]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default TeamManagementPanel;


